import { createSelector } from 'reselect';
import _find from 'lodash/find';
import _filter from 'lodash/filter';
import _has from 'lodash/has';
import _every from 'lodash/every';
import _sortBy from 'lodash/sortBy';
import _keys from 'lodash/keys';
import _last from 'lodash/last';
import _some from 'lodash/some';

import FeatureFlags from '../../util/featureFlags';

// Static Data
import {
  REQUIRED_OPTION_VALUE_KEYS,
  ORDERED_CUSTOMIZATION_TYPES,
  CUSTOMIZATION_BAR_DROPDOWN_SECTIONS,
  BACK_PRINTING_PRICE,
  ENABLED_SAMPLE_ORDER_CARD_TYPES,
  ENABLED_SAMPLE_ORDER_ALL_CARD_TYPES,
} from '../constants/Cards';
import { pageName } from '../util';


export const getActiveField = state => state.cards.project.ui.activeField;
export const getBreadcrumbs = state => state.cards.project.ui.breadcrumbs;
export const getCustomizations = state => state.cards.project.entities.customizations;
export const getCustomizationTypeUUIDs = state => state.cards.project.customizationTypeToUUID;
export const getElementEntities = state => state.cards.project.entities.elements;
export const getFonts = state => state.cards.project.fonts;
export const getIsAddingToCart = state => state.cards.project.isAddingToCart;
export const getPageEntities = state => state.cards.project.entities.pages;
export const getProjectBusy = state => state.cards.project.isFetching;
export const getProjectType = state => state.cards.project.type;
export const getProjectUUID = state => state.cards.project.uuid;
export const getProjectAccountID = state => state.cards.project.accountId;
export const getRSVPCustomizationUUID = state => state.cards.project.customizationTypeToUUID.RSVP;
export const getSize = state => state.cards.project.size;
export const getSplitOrderId = state => state.cards.project.splitOrderId;
export const getSteps = state => state.cards.project.ui.steps;
export const getProjectMedium = state => state.cards.project.medium;
export const getLandingPageVisibility = state => state.cards.project.landingPageVisibility;
export const getProjectMeta = state => state.cards.project.meta;
export const getProjectFoilStatus = state => state.cards.project.hasFoil;
export const getSeededCardSuite = state => state.cards.project.seededCardSuiteUUID;
export const getProjectInitializedCustomFoilStatus = state => state.cards.project.initializedCustomFoil;
export const getFoilHistory = state => state.cards.project.foilHistory;
export const getDeleteFoilHistory = state => state.cards.project.deletedFoilHistory;
export const getRSVPFoilHistory = state => state.cards.project.rsvpFoilHistory;
export const getRSVPDeleteFoilHistory = state => state.cards.project.rsvpDeletedFoilHistory;
export const getInitializedFoilColor = state => state.cards.project.initializedFoilColor;

export const getCurrentStep = (_, props) => {
  const { route, router, routes } = props;
  if (route && route.path) {
    return props.route.path;
  } if (router && router.location) {
    return props.router.location.pathname.split('/').pop();
  } if (routes) {
    const routesLength = routes.length;
    const lastRoute = routes[routesLength - 1];
    if (lastRoute && lastRoute.path) {
      return lastRoute.path;
    }
  }
  return null;
};

export const getCardType = createSelector(
  getSteps,
  getCurrentStep,
  (steps, currentStep) => {
    // temp workaround while digital animation pages use customization code
    if (
      currentStep === 'digitalOrder/:projectUUID'
      || currentStep === 'digitalPreview/:projectUUID'
      || currentStep === ':uuid'
    ) return 'SAVE_THE_DATE';
    return steps[currentStep] && steps[currentStep].cardType || 'SAVE_THE_DATE';
  }
);

export const getPageNumber = createSelector(
  getSteps,
  getCurrentStep,
  (steps, currentStep) => {
    // temp workaround while digital animation pages use customization code
    if (
      currentStep === 'digitalOrder/:projectUUID'
      || currentStep === 'digitalPreview/:projectUUID'
      || currentStep === ':uuid'
    ) return 0;
    return steps[currentStep].pageNumber;
  }
);

export const getCustomizationUUID = createSelector(
  getCustomizationTypeUUIDs,
  getCardType,
  (customizationTypeToUUID, cardType) => customizationTypeToUUID[cardType]
);

export const getLeadCustomization = createSelector(
  getProjectType,
  getCustomizationTypeUUIDs,
  getCustomizations,
  (projectType, customizationTypeToUUIDs, customizations) => {
    const uuid = customizationTypeToUUIDs[projectType];
    const leadCustomization = customizations[uuid];
    return leadCustomization;
  }
);

export const getLeadVariationOptionValues = createSelector(
  getLeadCustomization,
  (customization) => {
    if (customization && customization.variation && customization.variation.option_values) {
      return customization.variation.option_values;
    }
    return null;
  }
);

export const getQuantity = createSelector(
  getLeadCustomization,
  (customization) => {
    if (customization && customization.quantity) {
      return customization.quantity;
    }
    return 0;
  }
);

export const getCustomization = createSelector(
  getCustomizations,
  getCustomizationUUID,
  (customizations, customizationUUID) => customizations[customizationUUID]
);

export const getIsSampleOrder = createSelector(
  getCustomization,
  customization => !!customization && customization.quantity === 1
);

export const getPage = createSelector(
  getCustomization,
  getPageEntities,
  getPageNumber,
  (customization, pageEntities, pageNumber) => {
    const pages = customization.pages.map(pageUUID => pageEntities[pageUUID]);
    const page = _find(pages, { page_number: pageNumber + 1 });
    return page;
  }
);

export const getElements = createSelector(
  getPage,
  getElementEntities,
  (page, elementEntities) => page.elements.map(elementUUID => elementEntities[elementUUID])
);


export const getElementsByUUID = createSelector(
  getPage,
  getElementEntities,
  (page, elementEntities) => {
    const elementsByUUID = {};
    page.elements.forEach((elementUUID) => {
      elementsByUUID[elementUUID] = elementEntities[elementUUID];
    });
    return elementsByUUID;
  }
);

export const getElementUUIDs = createSelector(
  getPage,
  page => page.elements
);

export const getTextElements = createSelector(
  getElements,
  elements => elements.filter(element => element.content_type === 'TEXT')
);

export const getImageElements = createSelector(
  getElements,
  elements => elements.filter(element => element.content_type === 'IMAGE')
);

export const getCustomizationVariation = createSelector(
  getCustomization,
  customization => customization && customization.variation
);


export const getReversePrintingDisabled = createSelector(
  getCustomizationVariation,
  (variation) => {
    if (variation && variation.option_values) {
      return variation.option_values['reverse-printing'] && variation.option_values['reverse-printing'] === 'false';
    }
    return false;
  }
);

export const getUuid = createSelector(
  getProjectType,
  getCustomizationTypeUUIDs,
  (type, uuids) => uuids[type],
);

export const getCustomizationsWithValidOptionValues = createSelector(
  getCustomizations,
  getProjectMedium,
  (customizationsByUUID, medium) => {
    const customizations = _keys(customizationsByUUID).map(uuid => customizationsByUUID[uuid]);
    return customizations.filter((customization) => {
      if (customization && customization.variation) {
        const { variation } = customization;
        // DIGITAL does not use the 'paper-type' option
        const optionValueKeys = medium === 'PAPER' ? REQUIRED_OPTION_VALUE_KEYS : ['silhouette'];
        return _every(optionValueKeys, key => _has(variation.option_values, key));
      }
      return false;
    });
  }
);

export const getSelectedQuantity = createSelector(
  getUuid,
  getCustomizations,
  (uuid, customizations) => {
    if (customizations[uuid]) {
      return customizations[uuid].quantity;
    }
    return 100;
  }
);

const getCustomizationStep = (_, props) => {
  if (props && props.location && props.location.pathname) {
    // console.log('props: ', props);
    return _last(props.location.pathname.split('/'));
  }
  return null;
};

// This relies on ownProps.location to be connected so it requires that a component is
// hooked up with react router, this must be used for getting the currentStep during the
// customization flow, looking directly at url or using router state from redux causes
// bugs, the props do not update at the same time as the actual URL change or redux state change
export const getCurrentRoute = createSelector(
  getCustomizationStep,
  step => step
);

export const getRSVPCustomization = createSelector(
  getCustomizations,
  getRSVPCustomizationUUID,
  (customizations, rsvpCustomizationUUID) => customizations[rsvpCustomizationUUID]
);

export const getRSVPCustomizationDisabled = createSelector(
  getRSVPCustomization,
  (rsvpCustomization) => {
    if (rsvpCustomization) {
      return rsvpCustomization.inactive;
    }
    return false;
  }
);

export const getIsRecipientAddressingEnabled = createSelector(
  getSteps,
  getCustomizationTypeUUIDs,
  getCustomizations,
  (steps, customizationTypeToUUID, customizations) => {
    if (steps && steps.recipients && steps.recipients.cardType) {
      const uuid = customizationTypeToUUID[steps.recipients.cardType];
      const customization = customizations[uuid];
      if (customization) {
        return customization.recipient_addressing;
      }
    }
    return false;
  }
);

const isCustomizationActive = customization => (
  _has(customization, 'inactive')
    ? !customization.inactive
    : !customization.deleted_at
);

export const getIsCustomizationActive = createSelector(
  getCustomization,
  (customization) => {
    if (customization) {
      return isCustomizationActive(customization);
    }
    return null;
  }
);

export const getProjectOrderView = createSelector(
  getCustomizations,
  customizations => (
    _keys(customizations).reduce((result, uuid) => {
      const customization = customizations[uuid];

      if (customization && customization.variation) {
        const { variation } = customization;

        let cardType = customization.type;
        if (cardType === 'ENVELOPE') {
          if (/rsvp/i.test(customization.name)) {
            cardType = 'RSVP_ENVELOPE';
          } else {
            cardType = 'INVITATION_ENVELOPE';
          }
        }
        const urlSlug = pageName(cardType);

        // make sure not to include any deleted customizations in order view
        // getProjectOrderView is used in subtotal selector so it's important to filter out deleted
        // customizations to avoid messing up subtotal
        if (variation && isCustomizationActive(customization)) {
          const orderView = {
            customizationUUID: customization.uuid,
            name: customization.name,
            type: customization.type,
            quantity: customization.quantity,
            recipientAddressing: customization.recipient_addressing,
            urlSlug,
            priceCents: variation.price_cents,
            imageUrls: variation.image_urls,
            color: variation.option_values.color,
            size: variation.option_values.size,
            liningColor: variation.option_values['envelope-lining'] || 'none',
          };
          result.push(orderView);
        }
      }
      return _sortBy(result, o => ORDERED_CUSTOMIZATION_TYPES.indexOf(o.type));
    }, [])
  )
);

export const getOrderSubtotal = createSelector(
  getCustomizations,
  customizations => (
    _keys(customizations).reduce((subtotal, uuid) => {
      const customization = customizations[uuid];
      const { variation } = customization;
      let cost = 0;
      // make sure not to include any deleted or inactive customizations in subtotal
      if (customization && variation && isCustomizationActive(customization)) {
        cost = ((customization.quantity * variation.price_cents) / 100);
      }
      return subtotal + cost;
    }, 0)
  )
);

export const getProjectSubtotalSummary = createSelector(
  getCustomizations,
  getCustomizationTypeUUIDs,
  getSteps,
  getCurrentStep,
  getProjectType,
  (customizations, customizationTypeToUUIDs, steps, currentStep, projectType) => {
    const sections = CUSTOMIZATION_BAR_DROPDOWN_SECTIONS.reduce((result, section) => {
      if (steps[section.step]) {
        const customizationType = steps[section.step].cardType;
        const customizationUUID = customizationTypeToUUIDs[customizationType];
        if (customizationUUID) {
          const customization = customizations[customizationUUID];
          if (customization && isCustomizationActive(customization)) {
            const { variation } = customization;
            if (variation && variation.option_values) {
              let price = variation.price_cents ? (variation.price_cents / 100) : 0;
              let hasLiner;
              let customFoilColor;
              let { description, label } = section;
              if (section.step === 'detailFront') {
                // if reverse printing is enabled, subtract 35 cents (price of reverse-printing) from front of card
                // so that pricing information for each step is more precise
                // postcard sized customizations do not allow reverse-printing to be false so always attribute the entire price
                // to front of card
                if (
                  (variation.option_values['reverse-printing'] === 'true' && variation.option_values.size !== 'postcard') &&
                  (projectType !== 'CHANGE_THE_DATE')
                ) {
                  price -= (BACK_PRINTING_PRICE / 100);
                }
                if (variation.option_values['foil-color'] !== 'none') {
                  customFoilColor = variation.option_values['foil-color'];
                }
              } else if (section.step === 'detailBack') {
                if (
                  (variation.option_values['reverse-printing'] === 'true' && variation.option_values.size !== 'postcard') &&
                  (projectType !== 'CHANGE_THE_DATE')
                ) {
                  price = BACK_PRINTING_PRICE / 100;
                } else if (variation.option_values['reverse-printing'] === 'true' && variation.option_values.size === 'postcard') {
                  // need to have this separate condition so that the description indicates that there is reverse-printing
                  // enabled
                  price = 0;
                  description = 'Print on the back (required)';
                } else if (projectType === 'CHANGE_THE_DATE') {
                  // need to have this separate condition so that the description indicates that there is reverse-printing
                  // enabled but not required
                  price = 0;
                  description = 'Print on the back';
                } else {
                  price = 0;
                  description = 'No print on the back';
                }
              } else if (section.step === 'rsvp') {
                if (variation.option_values['foil-color'] !== 'none') {
                  customFoilColor = variation.option_values['foil-color'];
                }
              } else if (section.step === 'recipients') {
                if (projectType === 'HOLIDAY') {
                  label = 'Recipients';
                  description = 'Add addressing';
                }
                price = 0;
              } else if (section.step === 'envelope') {
                if (variation.option_values['envelope-lining'] !== 'none') {
                  hasLiner = true;
                }
              }
              const mergedSection = { ...section, price, description, label, hasLiner, customFoilColor, active: currentStep === section.step };
              result.push(mergedSection);
            }
          }
        }
      }
      return result;
    }, []);
    return sections;
  }
);

/**
 * Hacky way of checking if user has a sample order on cart
 * check for same name of product and if it's single item
 * @param {*} state
 * @param {*} props
 */
const sampleCheckoutState = (state, props) => {
  const { cart } = state;
  const { type, name, medium, hasFoil } = props;
  const foil = FeatureFlags.get('singleSampleNoFoil') && hasFoil;
  const supportedCards = (FeatureFlags.get('singleSampleAllCardTypes')) ? ENABLED_SAMPLE_ORDER_ALL_CARD_TYPES : ENABLED_SAMPLE_ORDER_CARD_TYPES;
  const checkoutState = {
    cardTypeEnabledForSample: (medium === 'MAGNET' || foil) ? false : supportedCards.includes(type),
    hasMultipleOfSameCard: false,
    hasOneOfSameCard: false
  };
  if (cart) {
    const { items, busy, size } = cart;
    if (items && size && size > 0 && name && !busy) {
      Object.assign(checkoutState, {
        hasOneOfSameCard: _some(cart.items, { name, quantity: 1 }),
        hasMultipleOfSameCard: _some(cart.items, { name })
      });
    }
  }
  return checkoutState;
};

export const makeGetSampleCheckoutState = () => {
  const getSampleCheckoutState = createSelector(
    [sampleCheckoutState],
    checkoutState => checkoutState
  );
  return getSampleCheckoutState;
};

export const getNoteFonts = createSelector(
  getFonts,
  (fonts) => {
    const rawNoteFonts = _filter(fonts.raw, 'guest_data_available');
    return {
      mapped: fonts.mappedNote,
      raw: rawNoteFonts,
    };
  }
);



// WEBPACK FOOTER //
// ./src/cards/selectors/cardProjectSelector.js